<template>
  <div>
    <div class="tracks-meter" :class="{ 'is-full': limitReached }">
      <LtIcon name="music" :size="18" />
      <span class="tracks-meter__txt">
        {{
          limitReached
            ? 'הגעת למקסימום הרצועות עבור ' + typeMeta.name
            : 'רצועות: ' + form.tracks.length + ' מתוך ' + typeMeta.trackLimit
        }}
      </span>
      <span class="tracks-meter__bar"><i :style="{ width: pct + '%' }"></i></span>
    </div>

    <div class="tracks-list">
      <div v-for="(t, i) in form.tracks" :key="i" class="track-row" :class="{ 'is-open': openIndex === i }">
        <div class="track-row__head" @click="toggle(i)">
          <span class="track-row__handle"><LtIcon name="grip" :size="16" /></span>
          <span class="track-row__num" :class="{ 'is-done': isTrackFilled(t) }">{{ i + 1 }}</span>
          <span class="track-row__title" :class="{ 'is-empty': !t.name }">
            {{ t.name || 'רצועה ' + (i + 1) + ' — ללא שם' }}
          </span>
          <span v-if="t.duration" class="track-row__chip">{{ t.duration }}</span>
          <button
            type="button"
            class="btn btn--link"
            style="color: var(--c-ink-500); padding: 4px 6px"
            @click.stop="remove(i)"
          >
            <LtIcon name="trash" :size="14" />
          </button>
          <span class="track-row__caret"><LtIcon name="chevron-down" :size="16" /></span>
        </div>
        <div v-if="openIndex === i" class="track-row__body">
          <div class="fields-row" style="margin-top: 14px">
            <div class="field">
              <label class="field__label">שם הרצועה <span class="req">*</span></label>
              <input v-model="t.name" class="input" placeholder="לדוגמה: שמש באמצע" />
            </div>
            <div class="field">
              <label class="field__label">תזמון</label>
              <input v-model="t.duration" class="input" placeholder="03:42" />
            </div>
          </div>
          <div class="fields-row">
            <div class="field">
              <label class="field__label">מילים <span class="req">*</span></label>
              <input v-model="t.writer" class="input" placeholder="לדוגמה: עינת שרוף" />
            </div>
            <div class="field">
              <label class="field__label">לחן <span class="req">*</span></label>
              <input v-model="t.composer" class="input" placeholder="לדוגמה: דניאל סלומון" />
            </div>
          </div>
          <div class="section-card" style="background: #fff">
            <div class="section-card__head">
              <h4 class="section-card__title">קבצי אודיו <FieldInfo field="tracks.audio" /></h4>
              <span class="section-card__hint">העלו קובץ מקור — ניתן להפיק את השאר אוטומטית</span>
            </div>

            <div class="audio-source-grid">
              <div
                v-for="fmt in visibleFormats"
                :key="fmt.key"
                class="audio-source"
                :class="{ 'is-filled': t.audio[fmt.key] }"
              >
                <span class="audio-source__icon">
                  <LtIcon :name="t.audio[fmt.key] ? 'check' : 'disc'" :size="20" />
                </span>
                <div class="audio-source__text">
                  <div class="audio-source__title">{{ fmt.name }}</div>
                  <div class="audio-source__sub">{{ t.audio[fmt.key] ? (t.audio[fmt.key].name || 'קובץ מועלה') : fmt.sub }}</div>
                </div>
                <div v-if="!t.audio[fmt.key] && !form.isPublished" class="audio-source__actions">
                  <button type="button" class="audio-source__btn" @click.stop="pickFile(i, fmt.key)">
                    <LtIcon name="upload" :size="14" /> העלאה
                  </button>
                  <button
                    v-if="canAutoGenerate(t, fmt.key)"
                    type="button"
                    class="audio-source__btn audio-source__btn--auto"
                    :class="{ 'is-loading': generatingKey === i + '_' + fmt.key }"
                    :disabled="generatingKey === i + '_' + fmt.key"
                    @click.stop="autoGenerate(i, fmt.key)"
                  >
                    <LtIcon :name="generatingKey === i + '_' + fmt.key ? 'loader' : 'wand'" :size="14" />
                    {{ generatingKey === i + '_' + fmt.key ? 'ממיר...' : 'צור אוטומטי' }}
                  </button>
                </div>
                <div v-if="t.audio[fmt.key]" class="audio-source__controls">
                  <button
                    v-if="fmt.key !== 'mp2'"
                    type="button"
                    class="audio-source__play"
                    @click.stop="togglePlay(t.audio[fmt.key])"
                  >
                    <LtIcon :name="playingId === t.audio[fmt.key].storage_id ? 'pause' : 'play'" :size="14" />
                  </button>
                  <button
                    v-if="!form.isPublished"
                    type="button"
                    class="audio-source__remove"
                    @click.stop="removeFile(i, fmt.key)"
                  >
                    <LtIcon name="trash" :size="14" />
                  </button>
                </div>
              </div>
            </div>
          </div>
          <div class="field">
            <label class="field__label">מילות השיר <span class="req">*</span></label>
            <div class="rich-text rich-text--rtl">
              <QuillEditor
                v-model:content="t.lyrics"
                content-type="html"
                theme="snow"
                :toolbar="lyricsToolbar"
                placeholder="הכניסו את מילות השיר כאן..."
                dir="rtl"
              />
            </div>
          </div>
        </div>
      </div>

      <button v-if="!form.isPublished" type="button" class="add-track-btn" :disabled="limitReached" @click="addTrack">
        <LtIcon name="plus" :size="18" />
        {{ limitReached ? 'הגעת למקסימום' : 'הוספת רצועה' }}
      </button>
    </div>
  </div>
</template>

<script>
import LtIcon from '../LtIcon.vue';
import FieldInfo from '../FieldInfo.vue';
import { wizardFormInject, hasRichText } from '../wizardInject.js';
import { QuillEditor } from '@vueup/vue-quill';
import '@vueup/vue-quill/dist/vue-quill.snow.css';

export default {
  name: 'StepTracks',
  mixins: [wizardFormInject],
  components: { LtIcon, FieldInfo, QuillEditor },
  props: {
    typeMeta: { type: Object, required: true },
  },
  data() {
    return {
      openIndex: 0,
      playingId: null,
      playerAudioEl: null,
      generatingKey: null,
      lyricsToolbar: [['bold', 'italic', 'underline']],
      audioFormats: [
        { key: 'wav', name: 'WAV', sub: 'איכות מאסטר', accept: 'audio/wav,.wav', ext: '.wav' },
        { key: 'mp3_320', name: 'MP3 320', sub: 'איכות גבוהה', accept: 'audio/mpeg,.mp3', ext: '.mp3' },
        { key: 'mp3_128', name: 'MP3 128', sub: 'איכות סטנדרטית', accept: 'audio/mpeg,.mp3', ext: '.mp3' },
        { key: 'mp2', name: 'MP2', sub: 'לרדיו', accept: '.mp2', ext: '.mp2' },
      ],
    };
  },
  computed: {
    limitReached() {
      return this.form.tracks.length >= this.typeMeta.trackLimit;
    },
    pct() {
      return Math.min(100, Math.round((this.form.tracks.length / this.typeMeta.trackLimit) * 100));
    },
    visibleFormats() {
      if (this.form.type === 'album') return this.audioFormats.filter(f => f.key !== 'wav');
      return this.audioFormats;
    },
  },
  methods: {
    addTrack() {
      if (this.limitReached) return;
      this.form.tracks.push({
        name: '',
        writer: '',
        composer: '',
        duration: '',
        lyrics: '',
        audio: { wav: null, mp3_320: null, mp3_128: null, mp2: null },
      });
      this.openIndex = this.form.tracks.length - 1;
    },
    remove(i) {
      this.form.tracks.splice(i, 1);
      if (this.openIndex >= this.form.tracks.length) {
        this.openIndex = this.form.tracks.length - 1;
      }
    },
    toggle(i) {
      this.openIndex = this.openIndex === i ? -1 : i;
    },
    hasRichText,
    isTrackFilled(t) {
      return !!(t.name && t.writer && t.composer && this.hasRichText(t.lyrics) && t.audio.mp3_320 && t.audio.mp3_128 && t.audio.mp2);
    },
    canAutoGenerate(t, key) {
      if (key === 'wav') return false;
      if (key === 'mp3_320') return !!t.audio.wav;
      return !!(t.audio.wav || t.audio.mp3_320);
    },
    pickFile(i, key) {
      const fmt = this.audioFormats.find(f => f.key === key);
      const input = document.createElement('input');
      input.type = 'file';
      input.accept = fmt.accept;
      input.onchange = (e) => {
        const file = e.target.files[0];
        if (!file) return;
        this.uploadFile(file, fmt.ext).then((res) => {
          if (!res) return;
          this.form.tracks[i].audio[key] = { storage_id: res.storage_id, name: res.name, size: res.size };
        }).catch(() => {});
      };
      input.click();
    },
    removeFile(i, key) {
      this.form.tracks[i].audio[key] = null;
    },
    autoGenerate(i, key) {
      const t = this.form.tracks[i];
      const sourceObj = t.audio.wav || t.audio.mp3_320;
      if (!sourceObj || !sourceObj.storage_id) return;
      this.generatingKey = i + '_' + key;
      this.api({
        action: 'my_linktones/convert_audio',
        data: { source_storage_id: sourceObj.storage_id, target_format: key, linktone_id: this.form.id },
      }, (res) => {
        this.generatingKey = null;
        const d = res && res.data ? res.data : res;
        if (d.error) { this.alert(d.error); return; }
        t.audio[key] = { storage_id: d.storage_id, name: d.name, size: d.size || 0 };
      }, () => {
        this.generatingKey = null;
        this.alert('שגיאה ביצירת הקובץ');
      });
    },
    togglePlay(audioObj) {
      if (!audioObj || !audioObj.storage_id) return;
      if (this.playingId === audioObj.storage_id) {
        if (this.playerAudioEl) { this.playerAudioEl.pause(); }
        this.playingId = null;
        return;
      }
      if (this.playerAudioEl) { this.playerAudioEl.pause(); }
      this.playingId = audioObj.storage_id;
      this.api({ method: 'get', action: 'my_linktones/get_audio_url', data: { storage_id: audioObj.storage_id } }, (res) => {
        const d = res && res.data ? res.data : res;
        if (d.error || !d.url) { this.playingId = null; return; }
        if (!this.playerAudioEl) {
          this.playerAudioEl = new Audio();
          this.playerAudioEl.addEventListener('ended', () => { this.playingId = null; });
        }
        this.playerAudioEl.src = d.url;
        this.playerAudioEl.play().catch(() => { this.playingId = null; });
      }, () => { this.playingId = null; });
    },
  },
};
</script>

<style scoped>
/* Rich text editor (vue-quill) */
.rich-text {
  background: #fff;
  border-radius: 8px;
}
.rich-text :deep(.ql-toolbar.ql-snow),
.rich-text :deep(.ql-container.ql-snow) {
  border-color: var(--c-border, #d9d9e3);
}
.rich-text :deep(.ql-toolbar.ql-snow) {
  border-top-left-radius: 8px;
  border-top-right-radius: 8px;
}
.rich-text :deep(.ql-container.ql-snow) {
  border-bottom-left-radius: 8px;
  border-bottom-right-radius: 8px;
  font-size: 14px;
}
/* The editable area carries the min-height so the whole box is clickable
   (the container only has min-height, so the editor's height:100% can't fill it). */
.rich-text :deep(.ql-editor) {
  min-height: 120px;
}
.rich-text--rtl :deep(.ql-container),
.rich-text--rtl :deep(.ql-editor) {
  direction: rtl;
}
.rich-text--rtl :deep(.ql-editor) {
  text-align: right;
}
.rich-text--rtl :deep(.ql-editor.ql-blank)::before {
  right: 15px;
  left: 15px;
  text-align: right;
  font-style: normal;
}
</style>
